import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AppService } from 'src/app/services/app.service';
import { IdbService } from 'src/app/services/idb.service';
@Component({
  selector: 'app-media-upload-progress',
  templateUrl: './media-upload-progress.component.html',
  styleUrls: ['./media-upload-progress.component.scss'],
  standalone: false
})
export class MediaUploadProgressComponent implements OnInit {

  @Input() taskId: string;
  @Input() orgId: string;

  pendingUploadFiles: any[] = [];
  estimatedTimeRemaining: string = '';

  private uploadSub?: { unsubscribe: () => void };
  private createdObjectUrls: string[] = [];
  private trackedFileIds: number[] = [];
  private batchCaptured = false;
  constructor(
    private modalCtrl: ModalController,
    private idbservice: IdbService,
    private _appServices: AppService
  ) { }

  ngOnInit() {
    this.watchUploads();
  }

  // private watchUploads() {
  //   this.uploadSub = this.idbservice
  //     .watchTaskFileDetailsToUpload(this.taskId, this.orgId)
  //     .subscribe((files: any[]) => {
  //       this.revokeCreatedObjectUrls();

  //       this.pendingUploadFiles = files.map(f => ({
  //         fileName: f.fileName,
  //         status:
  //           f.RetryHitting >= this._appServices.UploadMaxRetryHit
  //             ? 'Failed'
  //             : 'Uploading',
  //         thumbnailUrl: this.buildThumbnailUrl(f),
  //       }));

  //       this.updateEstimatedTime(files.length);

  //       if (files.length === 0) {
  //         // all done — dismiss and let caller continue
  //         this.modalCtrl.dismiss({ success: true });
  //       }
  //     });
  // }

  private watchUploads() {

    this.uploadSub = this.idbservice
      .watchTaskFileDetailsToUpload(this.taskId, this.orgId)
      .subscribe((files: any[]) => {
        console.log('[DEBUG] status samples:', files.slice(0, 3).map(f => ({
          fileName: f.fileName,
          UploadStatus: f.UploadStatus,
          type: typeof f.UploadStatus
        })));
        // Lock in the batch on first emission — only files not yet uploaded
        // at the moment the modal opened. Old completed files from earlier
        // sessions never get included.
        if (!this.batchCaptured) {
          this.trackedFileIds = files
            .filter(f => {
              const status = String(f.UploadStatus);
              return status === 'false' || status === 'syncing'; // only real pending states
            })
            .map(f => f.InspectionFileId);
          this.batchCaptured = true;
        }

        const relevantFiles = files.filter(f =>
          this.trackedFileIds.includes(f.InspectionFileId)
        );

        this.revokeCreatedObjectUrls();

        this.pendingUploadFiles = relevantFiles.map(f => ({
          fileName: f.fileName,
          status: this.getDisplayStatus(f),
          thumbnailUrl: this.buildThumbnailUrl(f),
        }));

        const stillPending = relevantFiles.filter(f => String(f.UploadStatus) !== 'true');
        this.updateEstimatedTime(stillPending.length);

        if (stillPending.length === 0 && relevantFiles.length > 0) {
          const anyFailed = relevantFiles.some(
            f => f.UploadStatus != 'true' && f.RetryHitting >= this._appServices.UploadMaxRetryHit
          );
          if (!anyFailed) {
            this.modalCtrl.dismiss({ success: true });
          }
        }
      });
  }

  private getDisplayStatus(f: any): 'Uploaded' | 'Uploading' | 'Queued' | 'Failed' {
    const status = String(f.UploadStatus);

    if (status === 'true') return 'Uploaded';
    if (f.RetryHitting >= this._appServices.UploadMaxRetryHit) return 'Failed';
    if (status === 'syncing') return 'Uploading';
    if (status === 'false') return 'Queued';
    return null; // undefined/legacy record — not part of this tracking system
  }

  hasFailedFiles(): boolean {
    return this.pendingUploadFiles.some(f => f.status === 'Failed');
  }

  async retryFailedUploads() {
    await this.idbservice.retryFailedFiles(
      this.taskId,
      this.orgId,
      this._appServices.UploadMaxRetryHit
    );
  }

  getFileIcon(fileName: string): string {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'heic'].includes(ext)) return 'image-outline';
    if (['mp4', 'mov', 'avi'].includes(ext)) return 'videocam-outline';
    if (ext === 'pdf') return 'document-text-outline';
    return 'document-outline';
  }

  getFileIconClass(fileName: string): string {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'heic'].includes(ext)) return 'icon-image';
    if (['mp4', 'mov', 'avi'].includes(ext)) return 'icon-video';
    if (ext === 'pdf') return 'icon-pdf';
    return 'icon-doc';
  }

  private updateEstimatedTime(remainingCount: number) {
    if (remainingCount === 0) {
      this.estimatedTimeRemaining = '';
    } else if (remainingCount <= 2) {
      this.estimatedTimeRemaining = 'Less than a minute';
    } else {
      this.estimatedTimeRemaining = `About ${Math.ceil(remainingCount / 2)} minute${remainingCount > 2 ? 's' : ''}`;
    }
  }

  private buildThumbnailUrl(fileRecord: any): string {
    try {
      if (!fileRecord.LocalFileBuffer) return 'assets/icons/file-generic.svg';
      const blob = new Blob([fileRecord.LocalFileBuffer], {
        type: fileRecord.LocalFileMime || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      this.createdObjectUrls.push(url);
      return url;
    } catch (e) {
      return 'assets/icons/file-generic.svg';
    }
  }

  private revokeCreatedObjectUrls() {
    this.createdObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.createdObjectUrls = [];
  }

  ngOnDestroy() {
    this.uploadSub?.unsubscribe();
    this.revokeCreatedObjectUrls();
  }
}
